import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useEntityFilter } from '../context/EntityFilterContext';
import TicketPreviewPanel from '../components/TicketPreviewPanel';
import TicketListView from './TicketList.view';
import { COMPLAINT_GROUP_LABEL } from '../constants/ticketOptions';

const PAGE_SIZE = 50;

// A complete, sane date filter value: full YYYY-MM-DD with year >= 2020.
// Prevents firing requests on partial keyboard input like 0002-07-01.
function isCompleteDate(v) {
  const m = String(v || '').match(/^(\d{4})-\d{2}-\d{2}$/);
  return !!m && parseInt(m[1], 10) >= 2020;
}

const PRIORITY_RANK = { critical: 4, high: 3, medium: 2, low: 1 };

// ── Helpers ────────────────────────────────────────────────────────────────────

function makeTktRef(hubspotId) {
  if (!hubspotId) return null;
  return 'TKT-' + String(hubspotId).slice(-5).padStart(5, '0');
}

// Map raw HubSpot ticket `entity` property values → human-readable entity names.
// HubSpot stores 'lofat_subscriptions' / 'lofat_restaurants' for Lofat tickets;
// portal slugs like 'lofat-lifestyle' are also accepted for safety.
const HS_ENTITY_DISPLAY = {
  'abraaj_water':        'Abraaj Water',
  'abraaj-water':        'Abraaj Water',
  'lofat_subscriptions': 'Lofat Lifestyle',
  'lofat_restaurants':   'Lofat Lifestyle',
  'lofat-lifestyle':     'Lofat Lifestyle',
  'lofat_lifestyle':     'Lofat Lifestyle',
};

// Explicit display-name -> entity_slug map for HubSpot-only rows. Explicit (not a
// string transform) so an unmapped entity fails safe (undefined -> EntityChip '—')
// instead of silently producing a valid-looking-but-wrong slug.
const HS_ENTITY_TO_SLUG = {
  'Abraaj Water':       'abraaj-water',
  'Lofat Lifestyle':    'lofat-lifestyle',
  'The Regency Kuwait': 'regency-kuwait',
  'Regency Kuwait':     'regency-kuwait',
};

// Merge portal + HubSpot tickets into one deduplicated, date-sorted list.
// Dedup key: portalTicket.hubspot_ticket_id === hsTicket.id (string comparison).
// HubSpot-only rows use ht.customer_name (populated by backend when contactId is known).
function mergeTickets(portalRows, hsRows, skipHsOnly, searchFilter, sortBy, sortOrder) {
  const hsUsed = new Set();
  const result = [];

  for (const pt of portalRows) {
    const hsId = pt.hubspot_ticket_id ? String(pt.hubspot_ticket_id) : null;
    const hsMatch = hsId ? hsRows.find(ht => String(ht.id) === hsId) : null;
    if (hsMatch) hsUsed.add(String(hsMatch.id));

    result.push({
      _key:               `portal-${pt.id}`,
      source:             hsMatch ? 'both' : 'portal',
      portalId:           pt.id,
      portalTicketNumber: pt.ticket_number || null,
      hubspotId:          hsMatch?.id ?? hsId,
      hubspotTicketRef:   hsMatch?.ticketRef ?? (hsId ? makeTktRef(hsId) : null),
      subject:            pt.subject || hsMatch?.subject || '',
      complaintGroup:     pt.complaint_group || hsMatch?.complaintGroup || '',
      complaintType:      pt.complaint_type || hsMatch?.complaintType || '',
      priority:           pt.complaint_priority || hsMatch?.priority || '',
      channelSource:      pt.channel_source || '',
      // Portal tickets have customer info; prefer portal values, fall back to HubSpot
      customerName:       pt.customer_name || hsMatch?.customer_name || '',
      customerMobile:     pt.customer_mobile || '',
      status:             pt.status || '',
      entityName:         pt.entity_name || '',
      entitySlug:         pt.entity_slug || null,
      assignedToName:     pt.assigned_to_name || '',
      description:        pt.complaint_description || hsMatch?.content || '',
      sortDate:           new Date(pt.created_at),
      createdAt:          pt.created_at,
      updatedAt:          pt.updated_at || null,
    });
  }

  // HubSpot-only rows (not matched to any portal ticket on this page).
  // D2: whenever any dropdown/date filter is active (status, priority, entity,
  // complaint group, dates — same definition as the backend), HubSpot-only rows
  // are skipped entirely: they have no portal status/group to filter on.
  const sf = (searchFilter || '').toLowerCase();

  for (const ht of hsRows) {
    if (skipHsOnly) break;
    if (hsUsed.has(String(ht.id))) continue;

    if (sf) {
      const hay = `${ht.subject} ${ht.complaintGroup} ${ht.complaintType} ${ht.customer_name || ''}`.toLowerCase();
      if (!hay.includes(sf)) continue;
    }

    result.push({
      _key:               `hs-${ht.id}`,
      source:             'hubspot',
      portalId:           null,
      portalTicketNumber: null,
      hubspotId:          ht.id,
      hubspotTicketRef:   ht.ticketRef,
      subject:            ht.subject || '',
      complaintGroup:     ht.complaintGroup || '',
      complaintType:      ht.complaintType || '',
      priority:           ht.priority || '',
      channelSource:      ht.channelSource || '',
      // customer_name / customer_mobile stored as HubSpot ticket properties
      // (written at creation time) and also returned via contactId lookup.
      customerName:       ht.customer_name   || '',
      customerMobile:     ht.customer_mobile || '',
      status:             '',
      entityName:         HS_ENTITY_DISPLAY[ht.entity] || ht.entity || '',
      entitySlug:         null,
      assignedToName:     '',
      description:        ht.content || '',
      sortDate:           ht.createdAt ? new Date(ht.createdAt) : new Date(0),
      createdAt:          ht.createdAt || '',
      updatedAt:          null,
    });
  }

  // Sort the merged list to match the server-side sort so HubSpot-only rows
  // slot in consistently. Default: created_at desc.
  const dir = sortOrder === 'asc' ? 1 : -1;
  result.sort((a, b) => {
    let cmp;
    if (sortBy === 'priority') {
      cmp = (PRIORITY_RANK[(a.priority || '').toLowerCase()] || 0) - (PRIORITY_RANK[(b.priority || '').toLowerCase()] || 0);
    } else if (sortBy === 'status') {
      cmp = (a.status || '').localeCompare(b.status || '');
    } else {
      cmp = a.sortDate - b.sortDate;
    }
    if (cmp === 0) cmp = a.sortDate - b.sortDate;
    return cmp * dir;
  });
  return result;
}

// Adapt a merged row to the presentational view's expected row shape.
function adaptRow(m) {
  const entity_slug = m.source === 'hubspot'
    ? (HS_ENTITY_TO_SLUG[m.entityName] ?? null)
    : (m.entitySlug ?? null);
  const groupLabel = COMPLAINT_GROUP_LABEL[m.complaintGroup] || m.complaintGroup || '';

  const dtSource = m.updatedAt || m.createdAt || null;
  let updatedRelative = '—';
  let updatedAbsolute = '';
  if (dtSource) {
    const d = new Date(dtSource);
    if (!isNaN(d.getTime())) {
      updatedRelative = formatDistanceToNowStrict(d, { addSuffix: true });
      if (updatedRelative.startsWith('0 seconds')) updatedRelative = 'just now';
      updatedAbsolute = d.toLocaleString();
    }
  }

  return {
    _key:            m._key,
    id:              m.portalTicketNumber || m.hubspotTicketRef || '—',
    entity_slug,
    entityName:      m.entityName,
    subject:         m.subject,
    group:           m.complaintGroup,
    groupLabel,
    priority:        m.priority || null,
    status:          m.status || '',
    hubspot:         m.source === 'hubspot',
    customer:        { name: m.customerName, mobile: m.customerMobile },
    channel:         m.channelSource,
    updatedRelative,
    updatedAbsolute,
    raw:             m,
  };
}

// ── Container ────────────────────────────────────────────────────────────────

export default function TicketList() {
  const { user } = useAuth();
  const { selectedEntity } = useEntityFilter();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Portal (PostgreSQL) state
  const [tickets, setTickets]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [entities, setEntities] = useState([]);
  const [previewRow, setPreviewRow] = useState(null); // slide-over for HubSpot-only rows
  const [filters, setFilters]   = useState({
    status:          searchParams.get('status')   || '',
    priority:        searchParams.get('priority') || '',
    search:          searchParams.get('search')   || '',
    complaint_group: '',
    entity_id:       '',
    date_from:       '',
    date_to:         '',
  });

  // Pagination + sorting (server-side via offset / sort_by / sort_order)
  const [offset, setOffset]       = useState(0);
  const [sortBy, setSortBy]       = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Search input is debounced 300ms into filters.search
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(prev => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Date inputs hold a local draft; only complete valid dates (year >= 2020)
  // are committed to filters (and therefore fire a request).
  const [dateDraft, setDateDraft] = useState({ date_from: '', date_to: '' });
  const setDateFilter = (key, value) => {
    setDateDraft(prev => ({ ...prev, [key]: value }));
    if (value === '' || isCompleteDate(value)) {
      setFilters(prev => (prev[key] === value ? prev : { ...prev, [key]: value }));
      setOffset(0);
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setSortOrder(col === 'created_at' ? 'desc' : 'asc');
    }
    setOffset(0);
  };

  // HubSpot state
  const [hsTickets, setHsTickets] = useState([]);
  const [hsLoading, setHsLoading] = useState(false);

  // Customer mobile lookup state (used to filter HubSpot tickets by contact)
  const [contactMobile, setContactMobile]             = useState('');
  const [contactId, setContactId]                     = useState('');
  const [contactNameDisplay, setContactNameDisplay]   = useState('');
  const [contactLookupStatus, setContactLookupStatus] = useState('idle'); // 'idle'|'loading'|'found'|'not_found'

  useEffect(() => {
    if (user?.role === 'super_admin') {
      api.get('/entities').then(r => setEntities(r.data)).catch(() => {});
    }
  }, [user]);

  // Entity slug for HubSpot query + portal query.
  // Precedence: page-local entity_id filter → header selector → user's own slug.
  const headerEntity = (selectedEntity && selectedEntity !== 'all') ? selectedEntity : '';
  const entitySlug = (() => {
    const fromFilter = entities.find(e => e.id === parseInt(filters.entity_id))?.slug || '';
    if (fromFilter) return fromFilter;
    if (headerEntity) return headerEntity;
    if (user?.role !== 'super_admin') return user?.entity_slug || '';
    return '';
  })();

  // Lookup a HubSpot contact by mobile to get contactId
  const lookupContact = async () => {
    const raw = contactMobile.trim();
    if (!raw) return;
    const mobile = raw.replace(/^\+?965/, '').trim();
    if (!mobile || mobile.length < 4) return;

    setContactLookupStatus('loading');
    setContactId('');
    setContactNameDisplay('');

    try {
      const slug = entitySlug || '';
      const res = await api.get(
        `/hubspot/customer?mobile=${encodeURIComponent(mobile)}&entity=${encodeURIComponent(slug)}`
      );
      if (res.data.found && res.data.customers?.length) {
        const c = res.data.customers[0];
        setContactId(c.contactId || '');
        setContactNameDisplay(c.name || c.contactId || '');
        setContactLookupStatus('found');
      } else {
        setContactLookupStatus('not_found');
      }
    } catch {
      setContactLookupStatus('idle');
    }
  };

  const clearContactLookup = () => {
    setContactMobile('');
    setContactId('');
    setContactNameDisplay('');
    setContactLookupStatus('idle');
  };

  // Portal load — paginated + sorted server-side, all filters server-side
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset, sort_by: sortBy, sort_order: sortOrder };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      // Header entity selector — server intersects against the user's allowed
      // set, so it's safe to send unconditionally when non-empty.
      if (headerEntity) params.entity = headerEntity;
      const res = await api.get('/tickets', { params });
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Portal tickets error:', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [filters, offset, sortBy, sortOrder, headerEntity]);

  // HubSpot load — entity + optional contactId.
  // dept_manager / dept_user only see portal tickets escalated to their department;
  // HubSpot-only rows are irrelevant and would show unfiltered results for them.
  const loadHsTickets = useCallback(async () => {
    if (user?.role === 'dept_manager' || user?.role === 'dept_user') {
      setHsTickets([]);
      return;
    }
    setHsLoading(true);
    try {
      const params = { limit: 50 };
      if (entitySlug) params.entity = entitySlug;
      if (contactId)  params.contactId = contactId;
      const res = await api.get('/hubspot/tickets', { params });
      setHsTickets(res.data.tickets || []);
    } catch (err) {
      console.error('HubSpot tickets error:', err);
      setHsTickets([]);
    } finally {
      setHsLoading(false);
    }
  }, [entitySlug, contactId, user?.role]);

  // Fetch both in parallel on mount and whenever filters / entity / contactId change
  useEffect(() => {
    load();
    loadHsTickets();
  }, [load, loadHsTickets]);

  // Any active dropdown/date filter (same set the backend treats as "active")
  // excludes HubSpot-only rows from the merged list — they have no portal
  // status/group and would leak into filtered results otherwise.
  const hsFilterActive = Boolean(
    filters.status || filters.priority || filters.entity_id ||
    filters.complaint_group || filters.date_from || filters.date_to
  );

  // Merged, deduplicated, sorted unified list
  const mergedTickets = useMemo(
    () => mergeTickets(tickets, hsTickets, hsFilterActive, filters.search, sortBy, sortOrder),
    [tickets, hsTickets, hsFilterActive, filters.search, sortBy, sortOrder]
  );

  // Adapt merged rows to the presentational view shape
  const adaptedRows = useMemo(() => mergedTickets.map(adaptRow), [mergedTickets]);

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0);
  };

  const clearFilters = () => {
    setFilters({ status: '', priority: '', search: '', complaint_group: '', entity_id: '', date_from: '', date_to: '' });
    setSearchInput('');
    setDateDraft({ date_from: '', date_to: '' });
    setOffset(0);
    clearContactLookup();
  };

  const isLoading = loading || hsLoading;

  // ── Thin handlers bridging to the view's callback contract ──
  const handleOpenTicket = (row) => {
    const raw = row.raw;
    if (raw.portalId) navigate(`/tickets/${raw.portalId}`);
    else if (raw.hubspotId) navigate(`/tickets/hs/${raw.hubspotId}`);
    else setPreviewRow(raw);
  };

  const handleContactMobileChange = (value) => {
    setContactMobile(value);
    if (!value.trim()) clearContactLookup();
  };

  const handlePage = (dir) => {
    if (dir === 'prev') setOffset(Math.max(0, offset - PAGE_SIZE));
    else setOffset(offset + PAGE_SIZE);
  };

  const handleNewTicket = () => navigate('/tickets/new');

  const handleRefresh = () => { load(); loadHsTickets(); };

  return (
    <>
      <TicketListView
        rows={adaptedRows}
        total={total}
        page={Math.floor(offset / PAGE_SIZE) + 1}
        pageSize={PAGE_SIZE}
        offset={offset}
        loading={isLoading}
        filters={filters}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        dateDraft={dateDraft}
        onDateDraftChange={setDateFilter}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={toggleSort}
        entities={entities}
        currentUserRole={user?.role}
        contactMobile={contactMobile}
        contactId={contactId}
        contactNameDisplay={contactNameDisplay}
        contactLookupStatus={contactLookupStatus}
        onContactMobileChange={handleContactMobileChange}
        onLookupContact={lookupContact}
        onClearContactLookup={clearContactLookup}
        onOpenTicket={handleOpenTicket}
        onNewTicket={handleNewTicket}
        onPage={handlePage}
        onRefresh={handleRefresh}
      />
      {previewRow && (
        <TicketPreviewPanel row={previewRow} onClose={() => setPreviewRow(null)} />
      )}
    </>
  );
}
